import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { catchError, defer, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { CuttingService, DivisionType, IConfig, IResponseConfig } from '../cutting.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { UserService } from '../user.service';
import { FailedImport, FailedParse, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Configuration';
const configId = 'configuration';

export const DIVISION_TYPES: DivisionType[] = ['BetweenEveryOptimizationGroup', 'BetweenEveryMaterial', 'BetweenEveryRule', 'Never'];

const EXAMPLE_COLOR_PAIRS: string[][] = [
	['Example Color A', 'Example Color B'],
	['Example Color C', 'Example Color D'],
];

export interface IConfigExportData {
	allowFrameAndSashInSameBasket?: boolean;
	allowMixedCrossSectionInSameBasket?: boolean;
	generateRodsForWaste?: boolean;
	extraRodsPercentage?: number;
	colors?: string[];
	machine?: string;
	divisionType?: DivisionType;
}

const ConfigSchema: IFileDataSchema<IConfigExportData> = {
	name: sheetName,
	properties: {
		allowFrameAndSashInSameBasket: {
			type: 'boolean',
			required: false,
			fileKey: 'AllowFrameAndSashInSameBasket',
			width: 30,
		},
		allowMixedCrossSectionInSameBasket: {
			type: 'boolean',
			required: false,
			fileKey: 'AllowMixedCrossSectionInSameBasket',
			width: 36,
		},
		generateRodsForWaste: {
			type: 'boolean',
			required: false,
			fileKey: 'GenerateRodsForWaste',
			width: 22,
		},
		extraRodsPercentage: {
			type: 'number',
			required: false,
			fileKey: 'ExtraRodsPercentage',
			width: 20,
		},
		colors: { type: { array: 'string' }, required: false, fileKey: 'Colors', width: 30 },
		machine: { type: 'string', required: false, fileKey: '(Machine)Name', width: 20 },
		divisionType: {
			type: 'string',
			required: false,
			fileKey: '(Machine)DivisionType',
			width: 24,
		},
	},
};

interface MergedConfigImport {
	allowFrameAndSashInSameBasket: boolean;
	allowMixedCrossSectionInSameBasket: boolean;
	generateRodsForWaste: boolean;
	extraRodsPercentage?: number;
	colors: string[][];
	machines: { name: string; divisionType: DivisionType }[];
}

function normalizeName(name: string): string {
	return name == null ? '' : String(name).trim().toLowerCase();
}

function hasDuplicateColor(combination: string[]): boolean {
	return new Set(combination.map(normalizeName)).size !== combination.length;
}

function resolveMachineName(self: string): string {
	return self.split('/').pop() || self;
}

// The settings columns only ever describe the whole configuration once, on one row.
// ExtraRodsPercentage is excluded from this check since it's nullable even on a valid settings row.
function hasScalarValues(row: IConfigExportData): boolean {
	return row.allowFrameAndSashInSameBasket !== undefined || row.allowMixedCrossSectionInSameBasket !== undefined || row.generateRodsForWaste !== undefined;
}

function buildExportRows(config: IResponseConfig): IConfigExportData[] {
	const colorPairs = config.prohibitedColorPairing ?? [];
	const machines = config.machines ?? [];
	const rowCount = Math.max(1, colorPairs.length, machines.length);
	const rows: IConfigExportData[] = [];

	for (let index = 0; index < rowCount; index++) {
		const row: IConfigExportData = {};

		if (index === 0) {
			row.allowFrameAndSashInSameBasket = config.allowFrameAndSashInSameBasket;
			row.allowMixedCrossSectionInSameBasket = config.allowMixedCrossSectionInSameBasket;
			row.generateRodsForWaste = config.generateRodsForWaste;
			row.extraRodsPercentage = config.extraRodsPercentage;
		}

		if (colorPairs[index]) {
			row.colors = colorPairs[index];
		}

		if (machines[index]) {
			row.machine = resolveMachineName(machines[index].self);
			row.divisionType = machines[index].divisionType;
		}

		rows.push(row);
	}

	return rows;
}

@Injectable({
	providedIn: 'root',
})
export class ConfigTransferService implements ITransferService<IResponseConfig> {
	private translationPrefix = 'masterdata.cutting.configImportExport';

	constructor(
		private cuttingService: CuttingService,
		private userService: UserService,
		private excelFileTransfer: ExcelFileTransfer<IConfigExportData>,
		private translator: TranslateService,
	) {}
	hasTemplate = (): boolean => true;
	getTemplate = (): void => this.downloadTemplate();

	public exportData(configs: IResponseConfig[]): Observable<IResponseConfig[]> {
		const config = configs[0];

		if (!config) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.missing-configuration`)));
		}

		const invalidCombinations = (config.prohibitedColorPairing ?? []).filter((combination) => hasDuplicateColor(combination));
		if (invalidCombinations.length > 0) {
			return throwError(
				() =>
					new Error(
						this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-color-in-combination`, {
							combinations: invalidCombinations.map((c) => c.join(', ')).join('; '),
						}),
					),
			);
		}

		const rows = buildExportRows(config);

		return defer(() => this.excelFileTransfer.exportData(sheetName, rows, ConfigSchema)).pipe(
			map(() => configs),
			catchError((error) => {
				throw new Error(
					this.translator.instant(`${this.translationPrefix}.export.errors.export-failed`, {
						error: error.message,
					}),
				);
			}),
		);
	}

	public downloadTemplate(): void {
		const rowCount = Math.max(DIVISION_TYPES.length, EXAMPLE_COLOR_PAIRS.length);
		const exampleRows: IConfigExportData[] = [];

		for (let index = 0; index < rowCount; index++) {
			const row: IConfigExportData = {};

			if (index === 0) {
				row.allowFrameAndSashInSameBasket = true;
				row.allowMixedCrossSectionInSameBasket = true;
				row.generateRodsForWaste = false;
				row.extraRodsPercentage = 5;
			}

			if (EXAMPLE_COLOR_PAIRS[index]) {
				row.colors = EXAMPLE_COLOR_PAIRS[index];
			}

			if (DIVISION_TYPES[index]) {
				row.machine = `Example Machine ${index + 1}`;
				row.divisionType = DIVISION_TYPES[index];
			}

			exampleRows.push(row);
		}

		this.excelFileTransfer.exportData(`${sheetName}Template`, exampleRows, ConfigSchema).subscribe();
	}

	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((fileRows) => {
				const merged = this.mergeRows(fileRows, ImportErrors);

				if (!merged || ImportErrors.length > 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				return this.cuttingService.getConfigs().pipe(
					mergeMap((configs) => {
						const existing = configs.items[0];
						const config = this.buildConfig(existing, merged);

						return this.cuttingService.updateConfig(config).pipe(
							map(
								() =>
									({
										successfulImports: [
											{
												Id: configId,
												status: ImportStatus.SuccessUpdate,
											} as SuccessFullImport,
										],
										errors: ImportErrors,
									}) as ImportResponse,
							),
							catchError((error) => {
								ImportErrors.push({
									Id: configId,
									status: ImportStatus.Failed,
									message: this.translator.instant(`${this.translationPrefix}.import.errors.update-failed${this.saveFailedErrorKeySuffix(error)}`),
								} as FailedImport);

								return of({
									successfulImports: [],
									errors: ImportErrors,
								} as ImportResponse);
							}),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<Array<IImportedData<IConfigExportData>>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, ConfigSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return of(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);

				return of([]);
			}
		};
	}

	private mergeRows(fileRows: Array<Array<IImportedData<IConfigExportData>>>, ImportErrors: ImportErrorTypes[]): MergedConfigImport | undefined {
		const importedRows = fileRows.flat();

		if (importedRows.length === 0) {
			// Only report "empty file" when nothing else already explains the lack of rows -
			// e.g. a raw parse failure already pushed its own, more specific error above.
			if (ImportErrors.length === 0) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: this.translator.instant(`${this.translationPrefix}.import.errors.empty-file`),
				} as FailedParse);
			}

			return undefined;
		}

		let settingsRowLocation: string | undefined;
		let allowFrameAndSashInSameBasket = false;
		let allowMixedCrossSectionInSameBasket = false;
		let generateRodsForWaste = false;
		let extraRodsPercentage: number | undefined;
		const colors: string[][] = [];
		const machines: { name: string; divisionType: DivisionType }[] = [];
		let hasErrors = false;

		importedRows.forEach(({ data: row, location }) => {
			if (hasScalarValues(row)) {
				if (settingsRowLocation !== undefined) {
					hasErrors = true;
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(`${this.translationPrefix}.import.errors.duplicate-settings-row`, { location, otherLocation: settingsRowLocation }),
					} as FailedParse);
				} else {
					settingsRowLocation = location;
					allowFrameAndSashInSameBasket = row.allowFrameAndSashInSameBasket ?? false;
					allowMixedCrossSectionInSameBasket = row.allowMixedCrossSectionInSameBasket ?? false;
					generateRodsForWaste = row.generateRodsForWaste ?? false;
					extraRodsPercentage = row.extraRodsPercentage;
				}
			}

			if (row.colors !== undefined) {
				const combination = row.colors.map((c) => (c ?? '').trim()).filter((c) => c.length > 0);

				if (combination.length === 0) {
					hasErrors = true;
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(`${this.translationPrefix}.import.errors.empty-color-combination`, { location }),
					} as FailedParse);
				} else if (hasDuplicateColor(combination)) {
					hasErrors = true;
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(`${this.translationPrefix}.import.errors.duplicate-color-in-combination`, { location }),
					} as FailedParse);
				} else {
					colors.push(combination);
				}
			}

			if (row.machine !== undefined || row.divisionType !== undefined) {
				const machineName = (row.machine ?? '').trim();

				if (!machineName || !row.divisionType) {
					hasErrors = true;
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(`${this.translationPrefix}.import.errors.incomplete-machine-row`, { location }),
					} as FailedParse);
				} else if (!DIVISION_TYPES.includes(row.divisionType)) {
					hasErrors = true;
					ImportErrors.push({
						status: ImportStatus.ParsingError,
						message: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-division-type`, { location, value: row.divisionType }),
					} as FailedParse);
				} else {
					machines.push({ name: machineName, divisionType: row.divisionType });
				}
			}
		});

		if (settingsRowLocation === undefined) {
			hasErrors = true;
			ImportErrors.push({
				status: ImportStatus.ParsingError,
				message: this.translator.instant(`${this.translationPrefix}.import.errors.missing-settings-row`),
			} as FailedParse);
		}

		if (hasErrors) {
			return undefined;
		}

		return {
			allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket,
			generateRodsForWaste,
			extraRodsPercentage,
			colors,
			machines,
		};
	}

	private buildConfig(existing: IResponseConfig, merged: MergedConfigImport): IConfig {
		const engine = this.userService.account?.defaultEngine;
		const existingSelfByName = new Map((existing.machines ?? []).map((m) => [normalizeName(resolveMachineName(m.self)), m.self]));

		return {
			self: existing.self,
			prohibitedColorPairing: merged.colors,
			allowFrameAndSashInSameBasket: merged.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: merged.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: merged.generateRodsForWaste,
			extraRodsPercentage: merged.extraRodsPercentage,
			machines: merged.machines.map((machine) => ({
				self: existingSelfByName.get(normalizeName(machine.name)) ?? `/mdm/apps/one/stores/${engine}/production-machines/${machine.name}`,
				divisionType: machine.divisionType,
			})),
		};
	}
	private saveFailedErrorKeySuffix(error: unknown): string {
		const status = (error as HttpErrorResponse)?.status;
		if (status === 400) {
			return '-bad-request';
		}
		if (status === 404) {
			return '-not-found';
		}
		return '';
	}
}
